export type CarbonScopeKey =
  | "broad" | "fuel" | "road" | "journey" | "diet" | "foodMiles"
  | "trees" | "land" | "gas" | "paris" | "ecologicalFootprint"
  | "biocapacity" | "overshoot" | "tipping" | "landReforestation";

export type CarbonResult = { label: string; value: string };

export type CarbonScopeProfile = {
  key: CarbonScopeKey;
  titles: string[];
  labels: [string, string, string];
  units: [string, string, string];
  defaults: [number, number, number];
  formula: string;
  articleSummary: string;
  relatedSlugs: [string, string];
  sourceNote?: string;
  calculate: (first: number, second: number, third: number) => CarbonResult[];
};

const fixed = (value: number, digits = 2) => new Intl.NumberFormat("en-GB", {
  maximumFractionDigits: digits,
}).format(value);

const kg = (value: number) => `${fixed(value)} kg CO₂e`;
const tonnes = (value: number) => `${fixed(value / 1000)} tonnes CO₂e`;

export const CARBON_SCOPE_PROFILES: Record<CarbonScopeKey, CarbonScopeProfile> = {
  broad: {
    key: "broad",
    titles: ["Carbon Footprint Calculator"],
    labels: ["Short-haul flights per year", "Car miles per week", "Home electricity"],
    units: ["return flights", "mi/wk", "kWh/yr"],
    defaults: [2, 150, 3500],
    formula: "Total CO₂e = flights + car + diet + home energy",
    articleSummary: "a full personal or household footprint across travel, diet, and home energy",
    relatedSlugs: ["ecology-electricity-cost-calculator", "ecology-home-heat-pump-cop-calculator"],
    calculate: () => [],
  },
  fuel: {
    key: "fuel",
    titles: ["Fuel Emissions Calculator"],
    labels: ["Fuel used", "Fuel emission factor", "Trips or fills"],
    units: ["litres", "kg CO₂e/L", "per period"],
    defaults: [50, 2.31, 1],
    formula: "Fuel emissions = litres used × fuel emission factor × trips or fills",
    articleSummary: "combustion emissions from a measured amount of fuel",
    relatedSlugs: ["ecology-commute-impact-calculator", "ecology-train-co2-calculator"],
    calculate: (litres, factor, periods) => {
      const total = Math.max(0, litres) * Math.max(0, factor) * Math.max(0, periods);
      return [{ label: "Fuel combustion emissions", value: kg(total) }, { label: "Per litre", value: kg(Math.max(0, factor)) }, { label: "Equivalent in tonnes", value: tonnes(total) }];
    },
  },
  road: {
    key: "road",
    titles: ["Commute Impact Calculator", "Car CO2 Calculator"],
    labels: ["Distance", "Fuel economy", "Travel days"],
    units: ["km one way", "L/100 km", "days/yr"],
    defaults: [18, 7.5, 220],
    formula: "Road CO₂e = round-trip distance × travel days × fuel use × 2.31 kg CO₂e/L",
    articleSummary: "road-transport emissions from distance, fuel economy, and travel frequency",
    relatedSlugs: ["ecology-fuel-emissions-calculator", "ecology-train-co2-calculator"],
    calculate: (distance, litresPer100, days) => {
      const km = Math.max(0, distance) * 2 * Math.max(0, days);
      const litres = km * Math.max(0, litresPer100) / 100;
      const total = litres * 2.31;
      return [{ label: "Annual road emissions", value: kg(total) }, { label: "Annual fuel used", value: `${fixed(litres)} L` }, { label: "Round-trip distance", value: `${fixed(km)} km/yr` }];
    },
  },
  journey: {
    key: "journey",
    titles: ["Flight CO2 Calculator", "Train CO2 Calculator", "Bus CO2 Calculator", "Cycling CO2 Calculator"],
    labels: ["Journey distance", "Return journeys", "Mode emission factor"],
    units: ["km one way", "per year", "kg CO₂e/passenger-km"],
    defaults: [500, 2, 0],
    formula: "Journey CO₂e = distance × 2 × return journeys × mode emission factor",
    articleSummary: "passenger-journey emissions for the selected travel distance and frequency",
    relatedSlugs: ["ecology-commute-impact-calculator", "ecology-fuel-emissions-calculator"],
    sourceNote: "Operational emissions only; excludes manufacture and food-energy emissions.",
    calculate: (distance, returns, factor) => {
      const passengerKm = Math.max(0, distance) * 2 * Math.max(0, returns);
      const total = passengerKm * Math.max(0, factor);
      return [{ label: "Journey emissions", value: kg(total) }, { label: "Passenger distance", value: `${fixed(passengerKm)} km` }, { label: "Equivalent in tonnes", value: tonnes(total) }];
    },
  },
  diet: {
    key: "diet",
    titles: ["Diet Carbon Calculator", "Beef CO2 Calculator", "Vegetarian vs Meat Calculator", "Vegan Impact Calculator"],
    labels: ["Meals or food amount", "Current food factor", "Lower-impact food factor"],
    units: ["servings/week", "kg CO₂e/serving", "kg CO₂e/serving"],
    defaults: [7, 5, 1.5],
    formula: "Annual food reduction = meals or amount × 52 × (current factor − lower-impact factor)",
    articleSummary: "food-related emissions and the difference between current and lower-impact choices",
    relatedSlugs: ["ecology-food-miles-calculator", "ecology-carbon-footprint-calculator"],
    calculate: (amount, current, lower) => {
      const annualCurrent = Math.max(0, amount) * 52 * Math.max(0, current);
      const annualLower = Math.max(0, amount) * 52 * Math.max(0, lower);
      return [{ label: "Current annual food emissions", value: kg(annualCurrent) }, { label: "Lower-impact scenario", value: kg(annualLower) }, { label: "Estimated reduction", value: kg(Math.max(0, annualCurrent - annualLower)) }];
    },
  },
  foodMiles: {
    key: "foodMiles",
    titles: ["Food Miles Calculator"],
    labels: ["Food transported", "Transport distance", "Transport emission factor"],
    units: ["kg", "km", "kg CO₂e/tonne-km"],
    defaults: [20, 800, 0.12],
    formula: "Food-mile CO₂e = food mass (tonnes) × transport distance × transport factor",
    articleSummary: "transport emissions for a defined mass of food over a defined distance",
    relatedSlugs: ["ecology-diet-carbon-calculator", "ecology-carbon-footprint-calculator"],
    calculate: (mass, distance, factor) => {
      const tonneKm = Math.max(0, mass) / 1000 * Math.max(0, distance);
      const total = tonneKm * Math.max(0, factor);
      return [{ label: "Transport emissions", value: kg(total) }, { label: "Freight work", value: `${fixed(tonneKm)} tonne-km` }, { label: "Per kg food", value: kg(mass > 0 ? total / mass : 0) }];
    },
  },
  trees: {
    key: "trees",
    titles: ["Tree Offset Calculator", "Tree Canopy Calculator"],
    labels: ["Annual emissions to address", "Sequestration rate", "Tree survival or canopy factor"],
    units: ["kg CO₂e/yr", "kg CO₂e/tree/yr", "%"],
    defaults: [1000, 25, 80],
    formula: "Trees needed = annual emissions ÷ (tree sequestration × survival or canopy factor)",
    articleSummary: "tree or canopy sequestration capacity against a stated emissions amount",
    relatedSlugs: ["ecology-carbon-sequestration-calculator", "ecology-carbon-footprint-calculator"],
    calculate: (emissions, rate, factor) => {
      const effectiveRate = Math.max(0, rate) * Math.max(0, factor) / 100;
      const treesNeeded = effectiveRate > 0 ? Math.ceil(Math.max(0, emissions) / effectiveRate) : 0;
      return [{ label: "Trees or canopy units needed", value: String(treesNeeded) }, { label: "Effective annual sequestration", value: kg(effectiveRate) }, { label: "Emissions addressed", value: kg(Math.max(0, emissions)) }];
    },
  },
  land: {
    key: "land",
    titles: ["Deforestation Rate Calculator", "Agroforestry Calculator", "Carbon Sequestration Calculator", "Soil Carbon Calculator"],
    labels: ["Land area", "Carbon change rate", "Project duration"],
    units: ["hectares", "tonnes CO₂e/ha/yr", "years"],
    defaults: [5, 4, 10],
    formula: "Land carbon change = area × annual carbon rate × project duration",
    articleSummary: "land-based carbon loss, restoration, sequestration, or soil-carbon change over a stated area and period",
    relatedSlugs: ["ecology-tree-offset-calculator", "ecology-carbon-footprint-calculator"],
    calculate: (area, rate, years) => {
      const total = Math.max(0, area) * Math.max(0, rate) * Math.max(0, years) * 1000;
      return [{ label: "Estimated carbon change", value: kg(total) }, { label: "Annual carbon change", value: tonnes(total / Math.max(1, years)) }, { label: "Project area", value: `${fixed(Math.max(0, area))} ha` }];
    },
  },
  landReforestation: {
    key: "landReforestation",
    titles: ["Reforestation Calculator"],
    labels: ["Land area", "Carbon change rate", "Project duration"],
    units: ["hectares", "tonnes CO₂e/ha/yr", "years"],
    defaults: [5, 4, 10],
    formula: "Land carbon change = area × annual carbon rate × project duration",
    articleSummary: "land-based carbon loss, restoration, sequestration, or soil-carbon change over a stated area and period",
    relatedSlugs: ["ecology-tree-offset-calculator", "ecology-carbon-footprint-calculator"],
    calculate: (area, rate, years) => {
      const total = Math.max(0, area) * Math.max(0, rate) * Math.max(0, years) * 1000;
      return [{ label: "Estimated carbon change", value: kg(total) }, { label: "Annual carbon change", value: tonnes(total / Math.max(1, years)) }, { label: "Project area", value: `${fixed(Math.max(0, area))} ha` }];
    },
  },
  gas: {
    key: "gas",
    titles: ["Methane Calculator", "Nitrous Oxide Calculator", "HFC Calculator", "SF6 Calculator", "Global Warming Potential Calculator", "CO2 Equivalent Calculator"],
    labels: ["Gas mass", "Global warming potential", "Measurement periods"],
    units: ["kg", "kg CO₂e/kg gas", "per period"],
    defaults: [10, 28, 1],
    formula: "CO₂e = gas mass × 100-year GWP factor × measurement periods (IPCC AR5)",
    articleSummary: "conversion of a named greenhouse gas amount into carbon-dioxide equivalent using a stated GWP factor",
    relatedSlugs: ["ecology-carbon-footprint-calculator", "ecology-paris-agreement-calculator"],
    sourceNote: "Source: DESNZ 2026 full-set workbook, “Refrigerant & other” sheet, rows 21, 22, 28, and 45. Methane 28; nitrous oxide 265; HFC-134a 1,300; SF₆ 23,500 kg CO₂e/kg. The workbook introduction identifies these GWP values as IPCC AR5 over 100 years.",
    calculate: (mass, gwp, periods) => {
      const total = Math.max(0, mass) * Math.max(0, gwp) * Math.max(0, periods);
      return [{ label: "Carbon-dioxide equivalent", value: kg(total) }, { label: "Equivalent in tonnes", value: tonnes(total) }, { label: "Applied GWP factor", value: `${fixed(Math.max(0, gwp))}×` }];
    },
  },
  paris: {
    key: "paris",
    titles: ["Paris Agreement Calculator"],
    labels: ["Current annual emissions", "Target reduction", "Years to target"],
    units: ["tonnes CO₂e/yr", "%", "years"],
    defaults: [100, 50, 10],
    formula: "Required annual reduction = current emissions × target reduction ÷ years to target",
    articleSummary: "a planning scenario for reducing a stated emissions baseline toward a stated target",
    relatedSlugs: ["ecology-carbon-footprint-calculator", "ecology-co2-equivalent-calculator"],
    calculate: (emissions, reduction, years) => {
      const cut = Math.max(0, emissions) * Math.max(0, reduction) / 100;
      return [{ label: "Target annual emissions", value: `${fixed(Math.max(0, emissions) - cut)} tonnes CO₂e` }, { label: "Total reduction required", value: `${fixed(cut)} tonnes CO₂e` }, { label: "Reduction per year", value: `${fixed(cut / Math.max(1, years))} tonnes CO₂e/yr` }];
    },
  },
  ecologicalFootprint: {
    key: "ecologicalFootprint",
    titles: ["Ecological Footprint Calculator"],
    labels: ["Resource demand", "Available biocapacity", "Population served"],
    units: ["global hectares", "global hectares", "people"],
    defaults: [3, 1.6, 1],
    formula: "Ecological overshoot = resource demand ÷ available biocapacity",
    articleSummary: "resource demand relative to the available biocapacity for the people represented",
    relatedSlugs: ["ecology-biocapacity-calculator", "ecology-earth-overshoot-calculator"],
    calculate: (demand, capacity, population) => {
      const ratio = Math.max(0, demand) / Math.max(0.0001, capacity);
      return [{ label: "Demand relative to biocapacity", value: `${fixed(ratio)} Earth-equivalents` }, { label: "Total demand", value: `${fixed(Math.max(0, demand) * Math.max(0, population))} gha` }, { label: "Biocapacity gap", value: `${fixed(Math.max(0, demand - capacity))} gha/person` }];
    },
  },
  biocapacity: {
    key: "biocapacity",
    titles: ["Biocapacity Calculator"],
    labels: ["Productive area", "Yield factor", "Population"],
    units: ["hectares", "global ha/hectare", "people"],
    defaults: [100, 1.2, 50],
    formula: "Biocapacity per person = productive area × yield factor ÷ population",
    articleSummary: "the productive biological capacity available per person from a defined area",
    relatedSlugs: ["ecology-ecological-footprint-calculator", "ecology-earth-overshoot-calculator"],
    calculate: (area, yieldFactor, population) => {
      const total = Math.max(0, area) * Math.max(0, yieldFactor);
      return [{ label: "Total biocapacity", value: `${fixed(total)} global ha` }, { label: "Biocapacity per person", value: `${fixed(total / Math.max(1, population))} global ha/person` }, { label: "People represented", value: fixed(Math.max(0, population), 0) }];
    },
  },
  overshoot: {
    key: "overshoot",
    titles: ["Earth Overshoot Calculator"],
    labels: ["Resource use", "One-Earth capacity", "Days in year"],
    units: ["Earth-equivalents", "Earth-equivalents", "days"],
    defaults: [1.7, 1, 365],
    formula: "Overshoot day = days in year × one-Earth capacity ÷ resource use",
    articleSummary: "a simple resource-use scenario expressed as an approximate annual overshoot date",
    relatedSlugs: ["ecology-ecological-footprint-calculator", "ecology-biocapacity-calculator"],
    calculate: (use, capacity, days) => {
      const day = Math.min(Math.max(1, Math.round(Math.max(0, days) * Math.max(0, capacity) / Math.max(0.0001, use))), Math.max(1, days));
      return [{ label: "Approximate overshoot day", value: `Day ${day} of ${fixed(Math.max(1, days), 0)}` }, { label: "Resource-use ratio", value: `${fixed(Math.max(0, use) / Math.max(0.0001, capacity))} Earths` }, { label: "Overshoot period", value: `${fixed(Math.max(0, days) - day, 0)} days` }];
    },
  },
  tipping: {
    key: "tipping",
    titles: ["Tipping Points Calculator"],
    labels: ["Temperature change", "Scenario threshold", "Exposure or sensitivity"],
    units: ["°C", "°C", "%"],
    defaults: [1.5, 2, 60],
    formula: "Scenario pressure = temperature change ÷ threshold × exposure factor",
    articleSummary: "a transparent scenario indicator for climate-system pressure, not a prediction of a real-world tipping event",
    relatedSlugs: ["ecology-paris-agreement-calculator", "ecology-carbon-footprint-calculator"],
    calculate: (temperature, threshold, exposure) => {
      const pressure = Math.max(0, temperature) / Math.max(0.01, threshold) * Math.max(0, exposure) / 100;
      return [{ label: "Scenario pressure indicator", value: `${fixed(pressure * 100)} / 100` }, { label: "Threshold headroom", value: `${fixed(Math.max(0, threshold - temperature))} °C` }, { label: "Interpretation", value: pressure >= 1 ? "At or above scenario threshold" : "Below scenario threshold" }];
    },
  },
};

export const CARBON_SCOPE_CLASSIFICATION = Object.values(CARBON_SCOPE_PROFILES)
  .flatMap((profile) => profile.titles.map((title) => ({ title, scope: profile.key, broad: profile.key === "broad" })));

export const SCOPED_CARBON_TITLES = CARBON_SCOPE_CLASSIFICATION
  .filter((item) => !item.broad)
  .map((item) => item.title);

export const CARBON_SCOPE_BY_TITLE = new Map(
  CARBON_SCOPE_CLASSIFICATION.map((item) => [item.title, item.scope]),
);

export function getCarbonScopeProfile(title?: string): CarbonScopeProfile {
  const scope = CARBON_SCOPE_BY_TITLE.get(title ?? "") ?? "broad";
  const profile = CARBON_SCOPE_PROFILES[scope];
  if (scope === "journey") {
    const travel: Record<string, { label: string; factor: number; formula: string; source: string }> = {
      "Flight CO2 Calculator": {
        label: "Flight factor (short-haul economy, with RF)",
        factor: 0.12576,
        formula: "Journey CO₂e = passenger-km × short-haul economy factor with radiative forcing",
        source: "Source: DESNZ 2026 full-set workbook, Business travel- air; Flights > Short-haul, to/from UK > Economy class; 0.12576 kg CO₂e/passenger.km, with radiative forcing (RF).",
      },
      "Train CO2 Calculator": {
        label: "National rail factor",
        factor: 0.03092,
        formula: "Journey CO₂e = passenger-km × national rail factor",
        source: "Source: DESNZ 2026 full-set workbook, Business travel- land; Rail > National rail; 0.03092 kg CO₂e/passenger.km.",
      },
      "Bus CO2 Calculator": {
        label: "Average local bus factor",
        factor: 0.10151,
        formula: "Journey CO₂e = passenger-km × average local bus factor",
        source: "Source: DESNZ 2026 full-set workbook, Business travel- land; Bus > Average local bus; 0.10151 kg CO₂e/passenger.km.",
      },
    };
    const selected = travel[title ?? ""];
    if (selected) {
      return {
        ...profile,
        labels: ["Journey distance", "Return journeys", selected.label],
        defaults: [500, 2, selected.factor],
        formula: selected.formula,
        sourceNote: selected.source,
      };
    }
  }
  if (scope !== "gas") return profile;

  const specific: Record<string, { label: string; gas: string; factor: number; row: number }> = {
    "Methane Calculator": { label: "Methane GWP100 factor", gas: "Methane", factor: 28, row: 21 },
    "Nitrous Oxide Calculator": { label: "Nitrous oxide GWP100 factor", gas: "Nitrous oxide", factor: 265, row: 22 },
    "HFC Calculator": { label: "HFC-134a GWP100 factor", gas: "HFC-134a", factor: 1300, row: 28 },
    "SF6 Calculator": { label: "SF₆ GWP100 factor", gas: "Sulphur hexafluoride (SF6)", factor: 23500, row: 45 },
  };
  const selected = specific[title ?? ""];
  return {
    ...profile,
    labels: ["Gas mass", selected?.label ?? "GWP100 factor (enter value for the named gas)", "Measurement periods"],
    units: ["kg", "kg CO₂e/kg gas", "per period"],
    defaults: [10, selected?.factor ?? 1, 1],
    formula: "CO₂e = gas mass × 100-year GWP factor × measurement periods (IPCC AR5)",
    sourceNote: selected
      ? `Source: DESNZ 2026 full-set workbook, “Refrigerant & other” sheet, ${selected.gas}, row ${selected.row}; ${selected.factor.toLocaleString("en-GB")} kg CO₂e/kg, IPCC AR5 100-year GWP.${title === "Methane Calculator" ? " No alternative methane value is listed in this workbook." : ""}`
      : profile.sourceNote,
  };
}